import { Element } from "./ElementsAccordion";
import { v4 as uuid } from "uuid";
import browser from "@assets/icons/canvas/browser.png";
import chat from "@assets/icons/canvas/chat.png";
import chart from "@assets/icons/canvas/chart.png";
import procent from "@assets/icons/canvas/procent.png";
import settings from "@assets/icons/canvas/settings.png";
import table from "@assets/icons/canvas/table.png";
import list from "@assets/icons/canvas/list.png";
import sankey from "@assets/icons/canvas/sankey.png";
import columns from "@assets/icons/canvas/columns.png";
import funnel from "@assets/icons/canvas/funnel.png";
import lightbulb from "@assets/icons/canvas/lightbulb.svg";
import browserThin from "@assets/icons/canvas/browser-thin.png";
import { ELEMENT_ID } from "src/store/canva/element-id";
import { FEATURE_TOGGLES, featureToggleManager } from "@lib/featureToggles";
import { useAllFeatureToggles } from "@hooks/useFeatureToggle";
import { useMemo } from "react";

const getFeatureKeyForElement = (elementId: ELEMENT_ID): string => {
  const elementToFeatureMap: Partial<Record<ELEMENT_ID, string>> = {
    [ELEMENT_ID.SUMMARY_AUDIT]: "CANVA_ELEMENT_SUMMARY_AUDIT",
    [ELEMENT_ID.EXECUTIVE_SUMMARY]: "CANVA_ELEMENT_EXECUTIVE_SUMMARY",
    [ELEMENT_ID.SALES_PERFORMANCE]: "CANVA_ELEMENT_SALES_PERFORMANCE",
    [ELEMENT_ID.SALES_CHANNEL]: "CANVA_ELEMENT_SALES_CHANNEL",
    [ELEMENT_ID.CUSTOMER_JOURNEY_MAP]: "CANVA_ELEMENT_CUSTOMER_JOURNEY_MAP",
    [ELEMENT_ID.PRODUCT_DETAILS]: "CANVA_ELEMENT_PRODUCT_DETAILS",
    [ELEMENT_ID.EXTERNAL_PRODUCT_DETAILS]:
      "CANVA_ELEMENT_EXTERNAL_PRODUCT_DETAILS",
    [ELEMENT_ID.SCORE_REVIEWS]: "CANVA_ELEMENT_SCORE_REVIEWS",
    [ELEMENT_ID.ASPECT_OPTIMIZATION]: "CANVA_ELEMENT_ASPECT_OPTIMIZATION",
    [ELEMENT_ID.CONTENT_OPTIMIZATION]: "CANVA_ELEMENT_CONTENT_OPTIMIZATION",
    [ELEMENT_ID.PRICE_OPTIMIZATION]: "CANVA_ELEMENT_PRICE_OPTIMIZATION",
    [ELEMENT_ID.REVIEWS_OPTIMIZATION]: "CANVA_ELEMENT_REVIEWS_OPTIMIZATION",
    [ELEMENT_ID.ADS_SALES_SLIDERS]: "CANVA_ELEMENT_ADS_SALES_SLIDERS",
    [ELEMENT_ID.SATURATION_CURVE]: "CANVA_ELEMENT_SATURATION_CURVE",
    [ELEMENT_ID.GOAL_SETTING]: "CANVA_ELEMENT_GOAL_SETTING",
    [ELEMENT_ID.ADS_SANKEY]: "CANVA_ELEMENT_ADS_SANKEY",
    [ELEMENT_ID.FUNNEL]: "CANVA_ELEMENT_FUNNEL",
    [ELEMENT_ID.PRODUCT_PLACEMENT]: "CANVA_ELEMENT_PRODUCT_PLACEMENT",
    [ELEMENT_ID.ADS_SALES]: "CANVA_ELEMENT_ADS_SALES",
    [ELEMENT_ID.CAMPAIGN_METRICS]: "CANVA_ELEMENT_CAMPAIGN_METRICS",
    [ELEMENT_ID.ORGANIC_SALES]: "CANVA_ELEMENT_ORGANIC_SALES",
    [ELEMENT_ID.ORGANIC_KEYWORD_PERFORMANCE]:
      "CANVA_ELEMENT_ORGANIC_KEYWORD_PERFORMANCE",
    [ELEMENT_ID.ORGANIC_MISSING_TARGET]: "CANVA_ELEMENT_ORGANIC_MISSING_TARGET",
    [ELEMENT_ID.FINANCIAL_OVERVIEW]: "CANVA_ELEMENT_FINANCIAL_OVERVIEW",
    [ELEMENT_ID.CASH_FLOW]: "CANVA_ELEMENT_CASH_FLOW",
    [ELEMENT_ID.WATERFALL]: "CANVA_ELEMENT_WATERFALL",
    [ELEMENT_ID.INVENTORY_PERFORMANCE]: "CANVA_ELEMENT_INVENTORY_PERFORMANCE",
    [ELEMENT_ID.INVENTORY_OVERVIEW]: "CANVA_ELEMENT_INVENTORY_OVERVIEW",
    [ELEMENT_ID.MARKET_LEVEL_SALES_DISTRIBUTION]:
      "CANVA_ELEMENT_MARKET_LEVEL_SALES_DISTRIBUTION",
    [ELEMENT_ID.MARKET_OVERVIEW]: "CANVA_ELEMENT_MARKET_OVERVIEW",
    [ELEMENT_ID.MARKET_SHARE]: "CANVA_ELEMENT_MARKET_SHARE",
    [ELEMENT_ID.MARKET_SALES_PERFORMANCE]:
      "CANVA_ELEMENT_MARKET_SALES_PERFORMANCE",
    [ELEMENT_ID.BRAND_SALES_PERFORMANCE]:
      "CANVA_ELEMENT_BRAND_SALES_PERFORMANCE",
    [ELEMENT_ID.LAUNCH_PRODUCT]: "CANVA_ELEMENT_LAUNCH_PRODUCT",
    [ELEMENT_ID.UNDEFINED]: "CANVA_ELEMENT_UNDEFINED",
    [ELEMENT_ID.WHAT_IF_CHART]: "CANVA_ELEMENT_WHAT_IF_CHART",
    [ELEMENT_ID.DEMOGRAPHIC_TYPE]: "CANVA_ELEMENT_DEMOGRAPHIC_TYPE",
    [ELEMENT_ID.DEMOGRAPHIC_TABLE]: "CANVA_ELEMENT_DEMOGRAPHIC_TABLE",
    [ELEMENT_ID.DEMOGRAPHIC_TYPE_SHARE]: "CANVA_ELEMENT_DEMOGRAPHIC_TYPE_SHARE",
    [ELEMENT_ID.BRAND]: "CANVA_ELEMENT_BRAND",
    [ELEMENT_ID.PNL_BLOCK]: "CANVA_ELEMENT_PNL_BLOCK",
    [ELEMENT_ID.TARGITING_PROFILES]: "CANVA_ELEMENT_TARGITING_PROFILES",
    [ELEMENT_ID.DATA_ENTRY]: "CANVA_ELEMENT_DATA_ENTRY",
  };

  return elementToFeatureMap[elementId] || "CANVA_ELEMENT_UNDEFINED";
};

const isElementEnabled = (elementId: ELEMENT_ID): boolean => {
  const featureKey = getFeatureKeyForElement(elementId);
  return featureToggleManager.isFeatureEnabled(featureKey as any);
};

const filterElementsByFeatureFlags = (elements: Element[]): Element[] => {
  return elements.filter((element) => isElementEnabled(element.elementId));
};

export const croElements: Element[] = [
  {
    id: uuid(),
    elementId: ELEMENT_ID.PRODUCT_DETAILS,
    name: "Product details",
    active: false,
    icon: browser,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.EXTERNAL_PRODUCT_DETAILS,
    name: "External product page",
    active: false,
    icon: browserThin,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.ASPECT_OPTIMIZATION,
    name: "Aspect optimization",
    active: false,
    icon: procent,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.SCORE_REVIEWS,
    name: "Reviews Score",
    active: false,
    icon: procent,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.CONTENT_OPTIMIZATION,
    name: "Content optimization",
    active: false,
    icon: settings,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.PRICE_OPTIMIZATION,
    name: "Price optimization",
    active: false,
    icon: settings,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.REVIEWS_OPTIMIZATION,
    name: "Reviews optimization",
    active: false,
    icon: chat,
  },
];

// Create reactive element lists that update when feature flags change
export const useFilteredCroElements = (): Element[] => {
  const allFeatureStates = useAllFeatureToggles();

  return useMemo(() => {
    return croElements.filter((element) => {
      const featureKey = getFeatureKeyForElement(element.elementId);
      // Use the feature toggle key, not the string key
      return (
        allFeatureStates[
          FEATURE_TOGGLES[featureKey as keyof typeof FEATURE_TOGGLES]?.key
        ] ?? false
      );
    });
  }, [allFeatureStates]);
};

export const useFilteredSummaryElements = (
  customerJourneyMapEnabled: boolean,
): Element[] => {
  const allFeatureStates = useAllFeatureToggles();

  return useMemo(() => {
    // Create base elements array
    const baseElements: Element[] = [
      {
        id: uuid(),
        elementId: ELEMENT_ID.EXECUTIVE_SUMMARY,
        name: "Executive summary",
        active: false,
        icon: table,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.SALES_PERFORMANCE,
        name: "Sales performance",
        active: false,
        icon: chart,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.SALES_CHANNEL,
        name: "Sales channel",
        active: false,
        icon: table,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.SUMMARY_AUDIT,
        name: "Summary of audit",
        active: false,
        icon: lightbulb,
      },
    ];

    if (customerJourneyMapEnabled) {
      baseElements.push({
        id: uuid(),
        elementId: ELEMENT_ID.CUSTOMER_JOURNEY_MAP,
        name: "Customer Journey Map",
        active: false,
        icon: sankey,
      });
    }

    // Filter by feature flags
    return baseElements.filter((element) => {
      const featureKey = getFeatureKeyForElement(element.elementId);
      // Use the feature toggle key, not the string key
      return (
        allFeatureStates[
          FEATURE_TOGGLES[featureKey as keyof typeof FEATURE_TOGGLES]?.key
        ] ?? false
      );
    });
  }, [allFeatureStates, customerJourneyMapEnabled]);
};

export const campaignElements: Element[] = [
  {
    id: uuid(),
    elementId: ELEMENT_ID.ADS_SALES_SLIDERS,
    name: "Ad Spend",
    active: false,
    icon: settings,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.SATURATION_CURVE,
    name: "Saturation Curve",
    active: false,
    icon: chart,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.GOAL_SETTING,
    name: "Goal Setting",
    active: false,
    icon: settings,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.ADS_SANKEY,
    name: "Ads Sankey",
    active: false,
    icon: sankey,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.FUNNEL,
    name: "Funnel Performance",
    active: false,
    icon: funnel,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.PRODUCT_PLACEMENT,
    name: "Product Placement",
    active: false,
    icon: columns,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.ADS_SALES,
    name: "Ad Sales",
    active: false,
    icon: list,
  },
];

export const useFilteredCampaignElements = (): Element[] => {
  const allFeatureStates = useAllFeatureToggles();

  return useMemo(() => {
    return campaignElements.filter((element) => {
      const featureKey = getFeatureKeyForElement(element.elementId);
      // Use the feature toggle key, not the string key
      return (
        allFeatureStates[
          FEATURE_TOGGLES[featureKey as keyof typeof FEATURE_TOGGLES]?.key
        ] ?? false
      );
    });
  }, [allFeatureStates]);
};

export const organicElements: Element[] = [
  {
    id: uuid(),
    elementId: ELEMENT_ID.ORGANIC_SALES,
    name: "Keyword Performance",
    active: false,
    icon: columns,
  },
];

export const useFilteredOrganicElements = (): Element[] => {
  const allFeatureStates = useAllFeatureToggles();

  return useMemo(() => {
    return organicElements.filter((element) => {
      const featureKey = getFeatureKeyForElement(element.elementId);
      // Use the feature toggle key, not the string key
      return (
        allFeatureStates[
          FEATURE_TOGGLES[featureKey as keyof typeof FEATURE_TOGGLES]?.key
        ] ?? false
      );
    });
  }, [allFeatureStates]);
};

export const financeElements: Element[] = [
  {
    id: uuid(),
    elementId: ELEMENT_ID.FINANCIAL_OVERVIEW,
    name: "Financial Overview",
    active: false,
    icon: table,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.CASH_FLOW,
    name: "Profit and Loss Statement",
    active: false,
    icon: table,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.WATERFALL,
    name: "Waterfall",
    active: false,
    icon: table,
  },
];

export const useFilteredFinanceElements = (): Element[] => {
  const allFeatureStates = useAllFeatureToggles();

  return useMemo(() => {
    return financeElements.filter((element) => {
      const featureKey = getFeatureKeyForElement(element.elementId);
      // Use the feature toggle key, not the string key
      return (
        allFeatureStates[
          FEATURE_TOGGLES[featureKey as keyof typeof FEATURE_TOGGLES]?.key
        ] ?? false
      );
    });
  }, [allFeatureStates]);
};

export const marketElements: Element[] = [
  {
    id: uuid(),
    elementId: ELEMENT_ID.MARKET_LEVEL_SALES_DISTRIBUTION,
    name: "Market Level Sales Distribution",
    active: false,
    icon: chart,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.MARKET_OVERVIEW,
    name: "Market Overview (bubble)",
    active: false,
    icon: chart,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.MARKET_SHARE,
    name: "Market Share (pie)",
    active: false,
    icon: chart,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.MARKET_SALES_PERFORMANCE,
    name: "Market Sales Performance",
    active: false,
    icon: list,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.BRAND_SALES_PERFORMANCE,
    name: "Brand Sales Performance",
    active: false,
    icon: chart,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.LAUNCH_PRODUCT,
    name: "Product Launch",
    active: false,
    icon: chart,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.UNDEFINED,
    name: "1",
    active: false,
    icon: chart,
  },
];

export const useFilteredMarketElements = (): Element[] => {
  const allFeatureStates = useAllFeatureToggles();

  return useMemo(() => {
    return marketElements.filter((element) => {
      const featureKey = getFeatureKeyForElement(element.elementId);
      // Use the feature toggle key, not the string key
      return (
        allFeatureStates[
          FEATURE_TOGGLES[featureKey as keyof typeof FEATURE_TOGGLES]?.key
        ] ?? false
      );
    });
  }, [allFeatureStates]);
};

export const supplyChainElements = [
  {
    id: uuid(),
    elementId: ELEMENT_ID.INVENTORY_PERFORMANCE,
    name: "Inventory Performance",
    active: false,
    icon: chart,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.INVENTORY_OVERVIEW,
    name: "Inventory Overview",
    active: false,
    icon: chart,
  },
];

export const useFilteredSupplyChainElements = (): Element[] => {
  const allFeatureStates = useAllFeatureToggles();

  return useMemo(() => {
    return supplyChainElements.filter((element) => {
      const featureKey = getFeatureKeyForElement(element.elementId);
      // Use the feature toggle key, not the string key
      return (
        allFeatureStates[
          FEATURE_TOGGLES[featureKey as keyof typeof FEATURE_TOGGLES]?.key
        ] ?? false
      );
    });
  }, [allFeatureStates]);
};

export const reportsElements: Element[] = [
  {
    id: "WHAT_IF_CHART",
    elementId: ELEMENT_ID.WHAT_IF_CHART,
    name: "Waterfall",
    active: false,
    icon: table,
  },
  {
    id: "DEMOGRAPHIC_TYPE",
    elementId: ELEMENT_ID.DEMOGRAPHIC_TYPE,
    name: "Demographic",
    active: false,
    icon: table,
  },
  {
    id: "BRAND",
    elementId: ELEMENT_ID.BRAND,
    name: "Brand",
    active: false,
    icon: table,
  },
  {
    id: "DATA_ENTRY",
    elementId: ELEMENT_ID.DATA_ENTRY,
    name: "Data Entry",
    active: false,
    icon: list,
  },
];

export const useFilteredReportsElements = (): Element[] => {
  const allFeatureStates = useAllFeatureToggles();

  return useMemo(() => {
    return reportsElements.filter((element) => {
      const featureKey = getFeatureKeyForElement(element.elementId);
      const stringKey =
        FEATURE_TOGGLES[featureKey as keyof typeof FEATURE_TOGGLES]?.key;
      console.log(
        `Checking feature state for ${element.elementId} (${featureKey}) -> ${stringKey}:`,
        allFeatureStates[stringKey],
      );
      // Use the feature toggle key, not the string key
      return allFeatureStates[stringKey] ?? false;
    });
  }, [allFeatureStates]);
};

const widgetSizeMap: Record<ELEMENT_ID, { width: number; height: number }> = {
  [ELEMENT_ID.SUMMARY_AUDIT]: { width: 8, height: 47 },
  [ELEMENT_ID.EXECUTIVE_SUMMARY]: { width: 8, height: 42 },
  [ELEMENT_ID.ADS_SALES]: { width: 8, height: 64 },
  [ELEMENT_ID.ORGANIC_KEYWORD_PERFORMANCE]: { width: 8, height: 72 },
  [ELEMENT_ID.ORGANIC_SALES]: { width: 8, height: 60 },
  [ELEMENT_ID.ADS_SANKEY]: { width: 8, height: 78 },
  [ELEMENT_ID.FUNNEL]: { width: 8, height: 76 },
  [ELEMENT_ID.SCORE_REVIEWS]: { width: 8, height: 70 },
  [ELEMENT_ID.ORGANIC_MISSING_TARGET]: { width: 8, height: 70 },
  [ELEMENT_ID.ADS_SALES_SLIDERS]: { width: 5, height: 30 },
  [ELEMENT_ID.CAMPAIGN_METRICS]: { width: 8, height: 80 },
  [ELEMENT_ID.PRODUCT_DETAILS]: { width: 5, height: 96 },
  [ELEMENT_ID.ASPECT_OPTIMIZATION]: { width: 2, height: 85 },
  [ELEMENT_ID.CONTENT_OPTIMIZATION]: { width: 2, height: 71 },
  [ELEMENT_ID.PRICE_OPTIMIZATION]: { width: 2, height: 71 },
  [ELEMENT_ID.REVIEWS_OPTIMIZATION]: { width: 3, height: 50 },
  [ELEMENT_ID.EXTERNAL_PRODUCT_DETAILS]: { width: 2, height: 51 },
  [ELEMENT_ID.DEMOGRAPHIC_TYPE]: { width: 10, height: 135 },
  [ELEMENT_ID.DEMOGRAPHIC_TABLE]: { width: 10, height: 43 },
  [ELEMENT_ID.PRODUCT_PLACEMENT]: { width: 8, height: 40 },
  [ELEMENT_ID.WHAT_IF_CHART]: { width: 10, height: 66 },
  [ELEMENT_ID.TARGITING_PROFILES]: { width: 12, height: 33 },
  [ELEMENT_ID.PNL_BLOCK]: { width: 8, height: 112 },
  [ELEMENT_ID.DEMOGRAPHIC_TYPE_SHARE]: { width: 15, height: 40 },
  [ELEMENT_ID.BRAND]: { width: 16, height: 100 },
  [ELEMENT_ID.DATA_ENTRY]: { width: 10, height: 80 },
  [ELEMENT_ID.SATURATION_CURVE]: { width: 8, height: 55 },
  [ELEMENT_ID.MARKET_LEVEL_SALES_DISTRIBUTION]: { width: 8, height: 68 },
  [ELEMENT_ID.MARKET_SHARE]: { width: 4, height: 68 },
  [ELEMENT_ID.SALES_VELICITY]: { width: 8, height: 55 },
  [ELEMENT_ID.MARKET_OVERVIEW_BUBBLE]: { width: 8, height: 55 },
  [ELEMENT_ID.MARKET_OVERVIEW]: { width: 4, height: 68 },
  [ELEMENT_ID.LAUNCH_PRODUCT]: { width: 8, height: 126 },
  [ELEMENT_ID.FUNNEL_MARKET]: { width: 8, height: 55 },
  [ELEMENT_ID.SALES_PERFORMANCE]: { width: 8, height: 55 },
  [ELEMENT_ID.GOAL_SETTING]: { width: 3, height: 25 },
  [ELEMENT_ID.LAUNCH_PRODUCT_TABLE]: { width: 8, height: 55 },
  [ELEMENT_ID.FINANCIAL_OVERVIEW]: { width: 3, height: 76 },
  [ELEMENT_ID.CASH_FLOW]: { width: 8, height: 55 },
  [ELEMENT_ID.PRODUCT_COST]: { width: 8, height: 55 },
  [ELEMENT_ID.WATERFALL]: { width: 8, height: 80 },
  [ELEMENT_ID.TOTAL_SALES]: { width: 8, height: 55 },
  [ELEMENT_ID.CONTRIBUTION_PROFIT]: { width: 8, height: 55 },
  [ELEMENT_ID.MARKET_SALES_PERFORMANCE]: { width: 8, height: 55 },
  [ELEMENT_ID.BRAND_SALES_PERFORMANCE]: { width: 8, height: 65 },
  [ELEMENT_ID.DATA_ENTRY_TABLE]: { width: 8, height: 55 },
  [ELEMENT_ID.CUSTOMER_JOURNEY_MAP]: { width: 8, height: 78 },
  [ELEMENT_ID.SALES_CHANNEL]: { width: 8, height: 55 },
  [ELEMENT_ID.INVENTORY_PERFORMANCE]: { width: 8, height: 55 },
  [ELEMENT_ID.INVENTORY_OVERVIEW]: { width: 8, height: 55 },
  [ELEMENT_ID.UNDEFINED]: { width: 8, height: 55 },
};

export const getWidgetSize = (
  elementId: ELEMENT_ID,
  brandsCount: number = 1,
): { width: number; height: number } => {
  if (elementId === ELEMENT_ID.BRAND) {
    const width = widgetSizeMap[elementId]?.width || 16;
    if (brandsCount > 0 && brandsCount < 3) {
      return { width, height: 27 * brandsCount };
    } else if (brandsCount >= 3) {
      return { width, height: 10 * brandsCount };
    }
  }

  return widgetSizeMap[elementId] || { width: 4, height: 60 };
};
